// =============================================================================
// LISTING USER AXIS EVIDENCE — the per-(USER × POSTING) evidence-scoring store
// (Lane L-H, FIX-E part 2; docs/design/evidence-scoring-2026-07-24.md;
// WALK-FINDINGS W18). The USER half of JobFitRadar's D24 two-layer read.
//
// WHY THIS EXISTS: R383 (listing_role_cornerstones) built the ROLE side — a real
// ≥6-axis cornerstone shape read straight from the posting. The scoring half was
// deliberately deferred, so the radar's USER side renders "not scored yet" ×N and
// the owner's walk read "the score of my experience doesn't have any overlap".
// This table holds the derived, GROUNDED evidence read: for each served role axis,
// a 0..1 evidence value backed by receipts (the real corpus achievement ids + the
// matched text) — EvidenceScoringService.scoreCorpusAgainstAxes.
//
// KEYED BY (userId, canonicalUrl) — UNIQUE. Unlike the role side (a property of the
// POSTING, shared cross-user), evidence is per-user: it reads THIS user's corpus.
// Store-once-FRESH: the row carries the freshness triple it was scored against
// (corpusEpoch + evidencePromptVersion + cornerstonePromptVersion); the serve path
// re-derives + upserts on any drift, because the user's corpus grows over time and
// serving stale evidence would itself break the honesty law (a bare store-once by
// (user,url,version) would go stale the moment a new achievement lands).
//
// NO INVENTION (CLAUDE.md foundational law, code-enforced): every served axis score
// > 0 carries at least one real achievement receipt. The grounding gate
// (EvidenceScoringService.enforceEvidenceGrounding) forces a receipt-less score to
// 0 — a "no evidence yet" axis (0, receipts empty), honestly DISTINCT from a
// "not scored yet" axis (this table has no row / the axis wasn't scored). A score
// this table never invents: it is arithmetic over already-persisted, real corpus
// achievements, exactly like AxisTailorLensService's own ranking.
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool
// (docs/design/evidence-scoring-2026-07-24.md's DDL — NOT applied by this lane),
// mirrored here in Drizzle and exported from index.ts.
// =============================================================================

import { sql } from 'drizzle-orm';
import { integer, jsonb, pgTable, text, uniqueIndex } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** One real corpus achievement that grounds an axis's evidence score. NEVER
 *  invented: `achievementId` is a live achievements.id, `matchedText` a snippet
 *  of that achievement's own text, `matchedTerms` the exact shared tokens the
 *  lexical match found, `affinityScore` the achievement's own persisted
 *  axis-affinity for this axis (null when only the lexical signal fired). At
 *  least one receipt is REQUIRED for an axis to serve evidence > 0. */
export interface EvidenceReceipt {
  achievementId: string;
  matchedText: string;
  matchedTerms: string[];
  affinityScore: number | null;
}

/** One served axis's grounded evidence read. `evidence` is 0..1; the grounding
 *  gate guarantees `evidence > 0` implies `receipts.length >= 1`. An axis scored
 *  with zero receipts serves `evidence: 0` + `receipts: []` — the honest
 *  "no evidence yet", distinct from an axis that was never scored at all. */
export interface AxisEvidenceScore {
  axisKey: string;
  evidence: number;
  receipts: EvidenceReceipt[];
}

export const listingUserAxisEvidence = pgTable(
  'listing_user_axis_evidence',
  {
    id: id(),
    /** The corpus owner (achievements.user_id convention — text-keyed to match
     *  the existing corpus tables; the text-keyed-user migration debt is
     *  pre-existing, never introduced here). */
    userId: text('user_id').notNull(),
    /** The posting this evidence was scored against — the same canonical_url the
     *  role-side listing_role_cornerstones / listing_axis_reads key on. */
    canonicalUrl: text('canonical_url').notNull(),
    /** The grounded per-axis evidence — AxisEvidenceScore[]. Only axes that were
     *  actually scored appear; each score > 0 carries >= 1 receipt (the gate). */
    axisEvidence: jsonb('axis_evidence')
      .$type<AxisEvidenceScore[]>()
      .notNull()
      .default(sql`'[]'::jsonb`),
    /** The role-side CORNERSTONE_PROMPT_VERSION the served axes came from — a
     *  re-derive key: a cornerstone re-extraction that changes the axis set must
     *  re-score the user against the new axes. */
    cornerstonePromptVersion: integer('cornerstone_prompt_version').notNull().default(1),
    /** This scorer's own version — bump when the matching algorithm changes in a
     *  way that makes a fresh score meaningful (mirrors the cornerstone row's
     *  own prompt_version reasoning). */
    evidencePromptVersion: integer('evidence_prompt_version').notNull().default(1),
    /** The corpus epoch (lib/corpusEpoch.ts, bump-on-corpus-write) this row was
     *  scored against — the freshness key. A stored row whose corpus_epoch !=
     *  the user's current epoch is stale (they grew their corpus) and is
     *  re-derived on read. Nullable only for a defensive legacy row. */
    corpusEpoch: text('corpus_epoch'),
    /** How many axes this row scored (= axisEvidence.length). Denormalized for a
     *  cheap telemetry read; never a source of truth over the array. */
    scoredAxisCount: integer('scored_axis_count').notNull().default(0),
    ...timestamps,
  },
  (t) => ({
    // UNIQUE (user_id, canonical_url) — store-once-per-(user,listing). The serve
    // path upserts on this target when the freshness triple drifts.
    userUrlIdx: uniqueIndex('listing_user_axis_evidence_user_url_idx').on(t.userId, t.canonicalUrl),
  }),
);
